import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Clock, 
  Check, 
  X,
  ArrowRight,
  Zap,
  Star,
  Crown,
  HelpCircle
} from 'lucide-react';

export const PricingPage = () => {
  const plans = [
    {
      name: "Free",
      description: "Perfect for trying out TeamTime",
      price: "0",
      period: "forever",
      icon: Zap,
      color: "slate",
      features: [
        { text: "Up to 5 employees", included: true },
        { text: "2 departments", included: true },
        { text: "1 branch/location", included: true },
        { text: "Basic time tracking", included: true },
        { text: "Simple scheduling", included: true },
        { text: "Leave management", included: true },
        { text: "Email notifications", included: true },
        { text: "Shift swaps", included: false },
        { text: "Advanced forecasting", included: false },
        { text: "Geo-fencing", included: false },
        { text: "PDF exports", included: false },
        { text: "Priority support", included: false },
      ],
      cta: "Get Started Free",
      popular: false
    },
    {
      name: "Pro",
      description: "For growing teams and businesses",
      price: "29.99",
      period: "per month",
      icon: Star,
      color: "orange",
      features: [
        { text: "Up to 50 employees", included: true },
        { text: "10 departments", included: true },
        { text: "5 branches/locations", included: true },
        { text: "Advanced time tracking", included: true },
        { text: "Smart scheduling", included: true },
        { text: "Leave management", included: true },
        { text: "Email notifications", included: true },
        { text: "Shift swaps", included: true },
        { text: "Advanced forecasting", included: true },
        { text: "Geo-fencing", included: true },
        { text: "PDF & CSV exports", included: true },
        { text: "Priority support", included: false },
      ],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      description: "For large organizations",
      price: "99.99",
      period: "per month",
      icon: Crown,
      color: "teal",
      features: [
        { text: "Unlimited employees", included: true },
        { text: "Unlimited departments", included: true },
        { text: "Unlimited branches", included: true },
        { text: "Advanced time tracking", included: true },
        { text: "Smart scheduling", included: true },
        { text: "Leave management", included: true },
        { text: "Email notifications", included: true },
        { text: "Shift swaps", included: true },
        { text: "Advanced forecasting", included: true },
        { text: "Geo-fencing", included: true },
        { text: "PDF & CSV exports", included: true },
        { text: "Priority support", included: true },
      ],
      cta: "Start Free Trial",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Can I try TeamTime before committing?",
      answer: "Absolutely! Our Free tier lets you explore most features with up to 5 employees. No credit card required."
    },
    {
      question: "Can I upgrade or downgrade anytime?",
      answer: "Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the next billing cycle."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards through Stripe. Caribbean customers can also pay in local currencies."
    },
    {
      question: "Is there a long-term contract?",
      answer: "No contracts! All plans are billed monthly and you can cancel anytime without penalties."
    }
  ];

  const getColorClasses = (color, type) => {
    const colors = {
      slate: {
        bg: "bg-slate-100",
        text: "text-slate-600",
        button: "bg-slate-900 hover:bg-slate-800",
        border: "border-slate-200",
        icon: "text-slate-600"
      },
      orange: {
        bg: "bg-orange-100",
        text: "text-orange-600",
        button: "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600",
        border: "border-orange-300",
        icon: "text-orange-600"
      },
      teal: {
        bg: "bg-teal-100",
        text: "text-teal-600",
        button: "bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600",
        border: "border-teal-200",
        icon: "text-teal-600"
      }
    };
    return colors[color][type];
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">TeamTime</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</Link>
              <Link to="/pricing" className="text-orange-600 font-medium">Pricing</Link>
              <Link to="/faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</Link>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:inline-flex">Log In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-orange-50/30 to-teal-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Choose the plan that fits your team. Start free and upgrade as you grow. No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative rounded-3xl p-8 ${
                  plan.popular 
                    ? 'bg-white shadow-2xl shadow-orange-500/10 border-2 border-orange-300 scale-105 z-10' 
                    : 'bg-white shadow-lg border border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className={`w-14 h-14 ${getColorClasses(plan.color, 'bg')} rounded-2xl flex items-center justify-center mb-6`}>
                  <plan.icon className={`w-7 h-7 ${getColorClasses(plan.color, 'icon')}`} />
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-slate-600 mb-6">{plan.description}</p>

                <div className="mb-8">
                  <span className="text-5xl font-bold text-slate-900">${plan.price}</span>
                  <span className="text-slate-500">/{plan.period}</span>
                </div>

                <Link to="/register">
                  <Button 
                    className={`w-full ${getColorClasses(plan.color, 'button')} text-white mb-8`}
                    size="lg"
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Have more questions? <Link to="/faq" className="text-orange-600 hover:text-orange-700 font-medium">View all FAQs</Link>
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Start with our free tier and upgrade when you're ready.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 shadow-xl">
              Start Your Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">TeamTime</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} TeamTime. All rights reserved.</p>
          <div className="flex gap-6 text-sm">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
